import { GRID_ROW_NUMBER_COL_DEF, type GridPinnedColumnFields, type GridSortModel } from '@repo/go-table';
import { action, computed, makeObservable, observable } from 'mobx';

type TRecommendationsTableSettingsModel = {
  columnsOrderModel?: string[];
  columnsVisibility?: Record<string, boolean>;
  columnsWidth?: Record<string, number>;
  sortingModel?: GridSortModel;
  pinnedColumns?: GridPinnedColumnFields;
};

export const SERVICE_COLUMNS = [GRID_ROW_NUMBER_COL_DEF.field];

class SettingsStorage {
  private readonly tableName = 'GOdashboard_Settings_Table_Recommendations';

  getSettings(): TRecommendationsTableSettingsModel {
    const settings = localStorage.getItem(this.tableName);
    if (!settings)
      return {
        columnsOrderModel: [],
        columnsVisibility: {},
        columnsWidth: {},
        sortingModel: [],
        pinnedColumns: {},
      };
    return JSON.parse(settings) as TRecommendationsTableSettingsModel;
  }

  setSettings(settings: TRecommendationsTableSettingsModel): void {
    localStorage.setItem(this.tableName, JSON.stringify(settings));
  }
}
export class RecommendationsDisplayManager {
  @observable private _settings: TRecommendationsTableSettingsModel;

  private readonly settingsStorage = new SettingsStorage();

  constructor() {
    const source = this.settingsStorage.getSettings();
    this._settings = {
      columnsOrderModel: source.columnsOrderModel ?? [],
      columnsVisibility: source.columnsVisibility ?? {},
      columnsWidth: source.columnsWidth ?? {},
      sortingModel: source.sortingModel ?? [],
      pinnedColumns: source.pinnedColumns,
    };
    makeObservable(this);
  }

  @computed
  get settings(): TRecommendationsTableSettingsModel {
    return this._settings;
  }

  @computed
  get columnsVisibilityModel(): Record<string, boolean> {
    return this._settings.columnsVisibility ?? {};
  }

  @computed
  get sortingModel(): GridSortModel {
    return this._settings.sortingModel ?? [];
  }

  @computed
  get columnsOrderModel(): string[] {
    return this._settings.columnsOrderModel ?? [];
  }

  @computed
  get pinnedColumns(): GridPinnedColumnFields | undefined {
    return this._settings.pinnedColumns;
  }

  getColumnsWidth(): Record<string, number> | undefined {
    return this._settings.columnsWidth;
  }

  @action.bound
  private updateSettings(newSettings: TRecommendationsTableSettingsModel) {
    this._settings = newSettings;
    this.settingsStorage.setSettings(newSettings);
  }

  @action.bound
  onColumnsOrderModelChange(columns: string[]): void {
    this.updateSettings({ ...this._settings, columnsOrderModel: columns });
  }

  @action.bound
  onSortChange(model: TRecommendationsTableSettingsModel['sortingModel']): void {
    this.updateSettings({ ...this._settings, sortingModel: model });
  }

  @action.bound
  onPinChange(pinnedColumns: TRecommendationsTableSettingsModel['pinnedColumns']): void {
    this.updateSettings({ ...this._settings, pinnedColumns });
  }

  @action.bound
  onWidthChange(incomeColumnsWidth: Record<string, number>): void {
    const newWidth = { ...this._settings.columnsWidth, ...incomeColumnsWidth };
    this.updateSettings({ ...this._settings, columnsWidth: newWidth });
  }

  @action.bound
  onColumnVisibilityChange(model: Record<string, boolean>): void {
    this.updateSettings({ ...this._settings, columnsVisibility: model });
  }
}
